import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { platformSalesOrderApi } from '../../api/platformSalesOrder';
import { message } from 'antd';

// 获取平台代售订单列表
export const fetchPlatformSalesOrderList = createAsyncThunk(
  'platformSalesOrder/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.getPlatformSalesOrderList(params);
      // 后端返回的是 IPage 格式，包含 records, total, current, size 等字段
      const data = response.data || response;
      return {
        list: data.records || data.list || [],
        total: data.total || 0,
        current: data.current || params.page || 1,
        pageSize: data.size || params.size || 10
      };
    } catch (error) {
      console.error('获取平台代售订单列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售订单列表失败' });
    }
  }
);

// 获取平台代售订单详情
export const fetchPlatformSalesOrderDetail = createAsyncThunk(
  'platformSalesOrder/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.getPlatformSalesOrderDetail(id);
      return response.data || response;
    } catch (error) {
      console.error('获取平台代售订单详情失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售订单详情失败' });
    }
  }
);

// 创建平台代售订单
export const createPlatformSalesOrder = createAsyncThunk(
  'platformSalesOrder/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.createPlatformSalesOrder(data);
      message.success('创建订单成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '创建订单失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 更新平台代售订单
export const updatePlatformSalesOrder = createAsyncThunk(
  'platformSalesOrder/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.updatePlatformSalesOrder(id, data);
      message.success('更新订单成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '更新订单失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 删除平台代售订单
export const deletePlatformSalesOrder = createAsyncThunk(
  'platformSalesOrder/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.deletePlatformSalesOrder(id);
      message.success('删除订单成功');
      return id;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除订单失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 同意代售申请
export const acceptOrder = createAsyncThunk(
  'platformSalesOrder/accept',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.acceptOrder(id);
      message.success('同意申请成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '同意申请失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 拒绝代售申请
export const rejectOrder = createAsyncThunk(
  'platformSalesOrder/reject',
  async ({ id, rejectReason }, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.rejectOrder(id, { rejectReason });
      message.success('拒绝申请成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '拒绝申请失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 确认送到平台
export const confirmOrder = createAsyncThunk(
  'platformSalesOrder/confirm',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.confirmOrder(id);
      message.success('确认收货成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '确认收货失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 退还代售商品
export const returnOrder = createAsyncThunk(
  'platformSalesOrder/return',
  async ({ id, returnReason, expressCompany, trackingNumber }, { rejectWithValue }) => {
    try {
      const response = await platformSalesOrderApi.returnOrder(id, { returnReason, expressCompany, trackingNumber });
      message.success('设置退货成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '设置退货失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 结算订单（打款给用户）
export const settleOrder = createAsyncThunk(
  'platformSalesOrder/settle',
  async ({ id, settleDTO }, { rejectWithValue, dispatch }) => {
    try {
      const response = await platformSalesOrderApi.settleOrder(id, settleDTO);
      if (!response || response.code !== 200) {
        const errorMsg = response?.message || '结算订单失败';
        message.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
      message.success('结算订单成功');
      // 重新获取订单详情以更新状态
      dispatch(fetchPlatformSalesOrderDetail(id));
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || '结算订单失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 平台代售订单 Slice
const platformSalesOrderSlice = createSlice({
  name: 'platformSalesOrder',
  initialState: {
    list: [],
    currentOrder: null,
    loading: false,
    error: null,
    pagination: {
      total: 0,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchPlatformSalesOrderList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlatformSalesOrderList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.list;
        state.pagination.total = action.payload.total;
        state.pagination.current = action.payload.current;
        state.pagination.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPlatformSalesOrderList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取详情
      .addCase(fetchPlatformSalesOrderDetail.fulfilled, (state, action) => {
        state.currentOrder = action.payload;
      })
      
      // 创建订单
      .addCase(createPlatformSalesOrder.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.pagination.total += 1;
      })
      
      // 更新订单
      .addCase(updatePlatformSalesOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 删除订单
      .addCase(deletePlatformSalesOrder.fulfilled, (state, action) => {
        state.list = state.list.filter(item => item.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentOrder && state.currentOrder.id === action.payload) {
          state.currentOrder = null;
        }
      })
      
      // 同意申请
      .addCase(acceptOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 拒绝申请
      .addCase(rejectOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 确认收货
      .addCase(confirmOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 退还商品
      .addCase(returnOrder.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      
      // 结算订单
      .addCase(settleOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(settleOrder.fulfilled, (state) => {
        state.loading = false;
        // 结算成功后会重新获取订单详情，这里不需要更新列表
      })
      .addCase(settleOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setPagination, clearCurrentOrder } = platformSalesOrderSlice.actions;
export default platformSalesOrderSlice.reducer;

